import Button from "@/atomic/atoms/Button";
import Header from "@/atomic/atoms/Header";
import { InputMask } from "@react-input/mask";
import { PropsView } from "./Models";
import Modal from "@/atomic/atoms/Modal";
import Loading from "@/atomic/atoms/Loading";
import { Suspense } from "react";
import useI18n from "@/hooks/useI18n";
import { useThemeContext } from "@/contexts/ThemeContext";

const inputBase =
  "w-full h-14 px-2 py-4 rounded-xl border font-primary text-base font-bold text-text bg-white placeholder:font-bold placeholder:text-text " +
  "focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary disabled:bg-disabled disabled:text-disabled-text";

function fieldClass(hasError?: boolean) {
  return [inputBase, hasError ? "border-error" : "border-border"].join(" ");
}

export default function PaymentAddressView({
  goBack,
  isDisabled,
  editable,
  isVisibleModal,
  toggleModal,
  isVisibleErrorModal,
  toggleErrorModal,
  isLoading,
  register,
  errors,
  handleSubmit,
}: PropsView) {
  const { t } = useI18n();
  const { themeName } = useThemeContext();
  const headerTitle =
    themeName === "zapay" ? "Pedágio" : t("pages:checkout:payment");

  return (
    <div className="h-full relative flex flex-col bg-white overflow-hidden">
      <Header title={headerTitle} hasLeftButton goBack={goBack} />
      <main className="flex-1 w-full flex justify-center min-h-0 bg-background">
        <form
          className="w-full max-w-[480px] px-5 pt-4 pb-8 flex flex-col flex-1 min-h-0"
          onSubmit={handleSubmit}
        >
          <div className="flex-1 min-h-0 overflow-y-auto pb-4">
            <h2 className="text-text text-2xl leading-[1.2] font-secondary font-medium tracking-[-0.2px]">
              {t("pages:paymentAddress:title")}
            </h2>
            <p className="mt-4 text-sm text-text-secondary font-primary leading-normal">
              {t("pages:paymentAddress:description")}
            </p>
            <p className="text-sm text-text-secondary font-primary leading-normal">
              {t("pages:paymentAddress:descriptionHint")}
            </p>

            <div className="mt-5 flex flex-col gap-4">
              <div>
                <label
                  htmlFor="payment-address-zipcode"
                  className="block mb-1 text-sm text-text font-primary"
                >
                  {t("pages:paymentAddress:cep")}
                </label>
                <InputMask
                  {...register("zipcode")}
                  id="payment-address-zipcode"
                  name="zipcode"
                  required
                  type="text"
                  inputMode="numeric"
                  mask="_____-___"
                  replacement={{ _: /\d/ }}
                  placeholder={t("pages:paymentAddress:placeholders:cep")}
                  className={fieldClass(!!errors.zipcode)}
                />
                {errors.zipcode && (
                  <span className="mt-1 block text-error text-xs font-primary">
                    {errors.zipcode.message as string}
                  </span>
                )}
              </div>

              <div className="flex flex-row gap-5 min-w-0">
                <div className="min-w-0 flex-[7]">
                  <label
                    htmlFor="payment-address-street"
                    className="block mb-1 text-sm text-text font-primary"
                  >
                    {t("pages:paymentAddress:street")}
                  </label>
                  <input
                    {...register("streetName")}
                    id="payment-address-street"
                    required
                    type="text"
                    name="streetName"
                    placeholder={t("pages:paymentAddress:placeholders:street")}
                    disabled={!editable}
                    className={fieldClass(!!errors.streetName)}
                  />
                  {errors.streetName && (
                    <span className="mt-1 block text-error text-xs font-primary">
                      {errors.streetName.message as string}
                    </span>
                  )}
                </div>
                <div className="min-w-0 flex-[3] max-w-[120px]">
                  <label
                    htmlFor="payment-address-number"
                    className="block mb-1 text-sm text-text font-primary"
                  >
                    {t("pages:paymentAddress:number")}
                  </label>
                  <input
                    {...register("number")}
                    id="payment-address-number"
                    required
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    name="number"
                    placeholder={t("pages:paymentAddress:placeholders:number")}
                    onInput={(e) => {
                      e.currentTarget.value = e.currentTarget.value.replace(
                        /\D/g,
                        ""
                      );
                    }}
                    className={fieldClass(!!errors.number)}
                  />
                  {errors.number && (
                    <span className="mt-1 block text-error text-xs font-primary">
                      {errors.number.message as string}
                    </span>
                  )}
                </div>
              </div>

              <div>
                <label
                  htmlFor="payment-address-complement"
                  className="block mb-1 text-sm text-text font-primary"
                >
                  {t("pages:paymentAddress:complement")}
                </label>
                <input
                  {...register("complement")}
                  id="payment-address-complement"
                  type="text"
                  name="complement"
                  placeholder={t(
                    "pages:paymentAddress:placeholders:complement"
                  )}
                  className={fieldClass(!!errors.complement)}
                />
                {errors.complement && (
                  <span className="mt-1 block text-error text-xs font-primary">
                    {errors.complement.message as string}
                  </span>
                )}
              </div>

              <div>
                <label
                  htmlFor="payment-address-neighborhood"
                  className="block mb-1 text-sm text-text font-primary"
                >
                  {t("pages:paymentAddress:neighborhood")}
                </label>
                <input
                  {...register("neighborhood")}
                  id="payment-address-neighborhood"
                  required
                  type="text"
                  name="neighborhood"
                  placeholder={t(
                    "pages:paymentAddress:placeholders:neighborhood"
                  )}
                  disabled={!editable}
                  className={fieldClass(!!errors.neighborhood)}
                />
                {errors.neighborhood && (
                  <span className="mt-1 block text-error text-xs font-primary">
                    {errors.neighborhood.message as string}
                  </span>
                )}
              </div>

              <div className="flex flex-row gap-5 min-w-0">
                <div className="min-w-0 flex-[7]">
                  <label
                    htmlFor="payment-address-city"
                    className="block mb-1 text-sm text-text font-primary"
                  >
                    {t("pages:paymentAddress:city")}
                  </label>
                  <input
                    {...register("city")}
                    id="payment-address-city"
                    required
                    type="text"
                    name="city"
                    placeholder={t("pages:paymentAddress:placeholders:city")}
                    disabled={!editable}
                    className={fieldClass(!!errors.city)}
                  />
                  {errors.city && (
                    <span className="mt-1 block text-error text-xs font-primary">
                      {errors.city.message as string}
                    </span>
                  )}
                </div>
                <div className="min-w-0 flex-[3] max-w-[120px]">
                  <label
                    htmlFor="payment-address-state"
                    className="block mb-1 text-sm text-text font-primary"
                  >
                    {t("pages:paymentAddress:state")}
                  </label>
                  <InputMask
                    {...register("state")}
                    id="payment-address-state"
                    name="state"
                    required
                    type="text"
                    mask="__"
                    replacement={{ _: /\D/ }}
                    placeholder={t("pages:paymentAddress:placeholders:state")}
                    disabled={!editable}
                    className={`${fieldClass(!!errors.state)} uppercase`}
                  />
                  {errors.state && (
                    <span className="mt-1 block text-error text-xs font-primary">
                      {errors.state.message as string}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="mt-8 shrink-0">
            <Button
              className="rounded-theme-large bg-primary text-white font-primary font-semibold border-0"
              title={t("pages:paymentAddress:saveAndContinue")}
              type="submit"
              isDisabled={isDisabled}
            />
          </div>
        </form>
      </main>
      {isVisibleModal && (
        <Suspense fallback={null}>
          <Modal
            title={t("pages:paymentAddress:errorTitle")}
            message={t("pages:paymentAddress:invalidCep")}
            actionMessage={t("pages:paymentAddress:useAnotherCep")}
            action={toggleModal}
          />
        </Suspense>
      )}
      {isVisibleErrorModal && (
        <Suspense fallback={null}>
          <Modal
            title={t("pages:paymentAddress:errorTitle")}
            message={t("pages:paymentAddress:errorMessage")}
            actionMessage={t("common:gotIt")}
            action={toggleErrorModal}
          />
        </Suspense>
      )}
      {isLoading && (
        <Suspense fallback={null}>
          <Loading />
        </Suspense>
      )}
    </div>
  );
}
